import { mount } from '../../../js/dom-lifecycle';
import { Chart } from '../charts-bundle';

export const resolveColor = (color) => color.startsWith('--')
    ? getComputedStyle(document.documentElement).getPropertyValue(color).trim()
    : color;

// MySQL DAYOFWEEK: 1=Sunday..7=Saturday → remap to 0=Monday..6=Sunday
const remapDow = (mysqlDow) => (mysqlDow + 5) % 7;

function buildLineConfig(data, aspectRatio) {
    return {
        type: 'line',
        data: {
            labels: data.labels,
            datasets: data.datasets.map(dataset => {
                const color = resolveColor(dataset.color);
                return {
                    label: dataset.label,
                    data: dataset.data,
                    borderColor: color,
                    backgroundColor: dataset.fill ? color + '30' : color,
                    fill: dataset.fill ?? false,
                    tension: 0.3,
                };
            })
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: aspectRatio ?? 2.6,
            plugins: {
                legend: { position: 'bottom' },
                zoom: {
                    zoom: {
                        drag: { enabled: true },
                        mode: 'x',
                    }
                }
            },
            scales: {
                y: { beginAtZero: true, ticks: { stepSize: 1 } }
            }
        }
    };
}

function buildBarConfig({ labels, data, color: baseColor }, aspectRatio) {
    const color = resolveColor(baseColor);
    return {
        type: 'bar',
        data: {
            labels,
            datasets: [{ data, backgroundColor: color, borderRadius: 4 }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: aspectRatio ?? 3,
            plugins: { legend: false },
            scales: {
                x: { grid: { display: false } },
                y: { beginAtZero: true }
            }
        }
    };
}

function hexToHsl(hex) {
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l * 100];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return [h * 60, s * 100, l * 100];
}

function generateScale(hex, count) {
    const [h, s, l] = hexToHsl(hex);
    const minL = Math.max(l - 40, 25);
    const maxL = Math.min(l + 40, 95);
    return Array.from({ length: count }, (_, i) => {
        const lightness = maxL - (i * (maxL - minL) / (count - 1));
        return `hsl(${h}, ${s}%, ${lightness}%)`;
    });
}

function buildPieConfig({ labels, data, color: baseColor }, aspectRatio) {
    const color = resolveColor(baseColor);
    const total = data.reduce((a, b) => a + b, 0);
    return {
        type: 'doughnut',
        data: {
            labels,
            datasets: [{ data, backgroundColor: generateScale(color, labels.length) }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: aspectRatio ?? 4 / 3,
            plugins: {
                legend: {
                    position: 'left',
                    labels: {
                        usePointStyle: true,
                        pointStyle: 'circle',
                        generateLabels: (chart) => chart.data.labels.map((label, i) => {
                            const value = chart.data.datasets[0].data[i];
                            const pct = total ? (value / total * 100).toFixed(1) : 0;
                            return {
                                text: `${label} – ${pct}%`,
                                fillStyle: chart.data.datasets[0].backgroundColor[i],
                                strokeStyle: chart.data.datasets[0].backgroundColor[i],
                                hidden: !chart.getDataVisibility(i),
                                index: i,
                            };
                        })
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            const pct = total ? (ctx.raw / total * 100).toFixed(1) : 0;
                            return `${ctx.label}: ${ctx.raw} (${pct}%)`;
                        }
                    }
                }
            }
        }
    };
}

function buildMatrixConfig({ data, dayLabels, color: baseColor }, aspectRatio) {
    const color = resolveColor(baseColor);
    const [h, s, l] = hexToHsl(color);
    const minL = Math.max(l - 40, 25);
    const maxL = Math.min(l + 40, 95);
    const max = Math.max(...data.map(d => d.v), 1);

    return {
        type: 'matrix',
        data: {
            datasets: [{
                data: data.map(d => ({ x: d.x, y: remapDow(d.y), v: d.v })),
                backgroundColor: (ctx) => {
                    const v = ctx.dataset.data[ctx.dataIndex]?.v ?? 0;
                    const lightness = maxL - (v / max) * (maxL - minL);
                    return `hsl(${h}, ${s}%, ${lightness}%)`;
                },
                width: ({ chart }) => (chart.chartArea?.width ?? 0) / 24 - 2,
                height: ({ chart }) => (chart.chartArea?.height ?? 0) / 7 - 2,
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: aspectRatio ?? 3,
            plugins: {
                legend: false,
                tooltip: {
                    callbacks: {
                        title: () => '',
                        label: (ctx) => {
                            const d = ctx.dataset.data[ctx.dataIndex];
                            return `${dayLabels[d.y]} ${d.x}:00 — ${d.v}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    type: 'linear',
                    position: 'top',
                    offset: true,
                    min: 0,
                    max: 23,
                    ticks: { stepSize: 1, callback: (v) => `${v}:00` },
                    grid: { display: false },
                },
                y: {
                    type: 'linear',
                    offset: true,
                    min: 0,
                    max: 6,
                    reverse: true,
                    ticks: { stepSize: 1, callback: (v) => dayLabels[v] ?? '' },
                    grid: { display: false },
                }
            }
        }
    };
}

mount('[data-chart]', function () {
    const data = JSON.parse($(this).find('[data-ref=data]').text());
    const type = $(this).data('chart');

    const aspectRatio = $(this).data('aspect-ratio');
    const configs = { matrix: buildMatrixConfig, bar: buildBarConfig, pie: buildPieConfig, line: buildLineConfig };
    const chart = new Chart(this, (configs[type] ?? buildLineConfig)(data, aspectRatio));

    if (type === 'line') {
        this.addEventListener('dblclick', () => chart.resetZoom());
    }
});
